/**
 * Mensagens de erro em português da Calculadora Completa (Fase 2, §8.5/§9.8).
 *
 * O back devolve `{ success:false, msg }` com texto cru (às vezes em inglês —
 * origem provável do "invalid interval"). Aqui traduzimos as chaves conhecidas
 * e usamos um fallback genérico para o resto; msg desconhecida vai ao Sentry
 * (tag juscalc-completa) para alimentar este mapa. Erros tipados são PB-08.
 */
import { capturarAviso } from './errosTelemetria';

export const FALLBACK_CALCULO =
  'Não foi possível concluir o cálculo. Verifique os dados e tente novamente.';

/** Chaves/textos conhecidos do back (`response.data.msg`). Chave em minúsculas. */
export const MENSAGENS_BACK: Record<string, string> = {
  reached_limit:
    'Você atingiu o limite de cálculos do seu plano. Escolha um plano para continuar.',
  'invalid interval':
    'Há um período com data inicial posterior à data final. Verifique as datas dos índices e dos juros e corrija.',
  'invalid date':
    'Há uma data inválida no cálculo. Verifique as datas e corrija.',
  'index not found':
    'Um dos índices selecionados não foi encontrado. Selecione o índice novamente.',
  'installments required':
    'Nenhuma parcela cadastrada. Adicione ao menos uma parcela no Passo 2.',
};

/** Chaves de `MENSAGENS_BACK` da mais longa para a mais curta (casamento parcial determinístico). */
export const CHAVES_POR_ESPECIFICIDADE = Object.keys(MENSAGENS_BACK).sort(
  (a, b) => b.length - a.length,
);

export interface MsgTraduzida {
  texto: string;
  conhecida: boolean;
}

export const traduzirMsgBack = (msg: unknown): MsgTraduzida => {
  if (typeof msg !== 'string' || msg.trim() === '') {
    return { texto: FALLBACK_CALCULO, conhecida: false };
  }
  const chave = msg.trim().toLowerCase();
  const direta = MENSAGENS_BACK[chave];
  if (direta) return { texto: direta, conhecida: true };
  // casamento por prefixo/inclusão (ex.: "Invalid interval: 2026-01-01 > 2025-01-01").
  // Da chave mais longa para a mais curta: sem a ordenação o resultado dependeria da
  // ordem de inserção do objeto e uma chave prefixo de outra roubaria o casamento.
  const parcial = CHAVES_POR_ESPECIFICIDADE.find((k) => chave.includes(k));
  if (parcial) return { texto: MENSAGENS_BACK[parcial], conhecida: true };
  return { texto: FALLBACK_CALCULO, conhecida: false };
};

/**
 * Traduz e, se desconhecida, registra no Sentry (msg como extra — não é PII,
 * é texto do servidor). Usado na saga `reportCorrectionComplete`.
 *
 * AA4 (PB-08): aceita a `msg` crua (comportamento atual) OU o objeto de erro
 * inteiro (`{ codigo, msg, detalhes }`), que passa por `interpretarErroBack`.
 */
export const mensagemErroCalculo = (msgOuData: unknown): string => {
  const { texto, conhecida } =
    msgOuData && typeof msgOuData === 'object'
      ? interpretarErroBack(msgOuData)
      : traduzirMsgBack(msgOuData);
  if (!conhecida) {
    capturarAviso(
      'juscalc-completa: msg de erro do back não mapeada',
      'msg-back-desconhecida',
      {
        // BB12 (ADR-032): o corte e a redação de dígitos são de `erros/redigir.ts`, aplicados
        // dentro do `capturarAviso` — aqui só entregamos o texto cru.
        msg:
          typeof msgOuData === 'string'
            ? msgOuData
            : String((msgOuData as ErroBack | null)?.msg ?? msgOuData),
      },
    );
  }
  return texto;
};

// ---------------- §9.8 — erros de sistema ----------------

/** §9.8 literal: falha ao buscar série de índice. */
export const msgSerieIndisponivel = (
  indice: string,
  dataInicial: string,
  dataFinal: string,
) =>
  `Não foi possível obter a série ${indice} do período ${dataInicial} a ${dataFinal}. Verifique sua conexão e tente novamente. Se o problema persistir, contate o suporte.`;

/** §9.8 literal: índice ainda não publicado (disparo depende de PB-08). */
export const msgIndiceNaoPublicado = (indice: string, mesAno: string) =>
  `O ${indice} de ${mesAno} ainda não foi divulgado pelo IBGE. Considere usar o índice provisório, ajustar a Data do cálculo para o último mês com dado disponível, ou aguardar a divulgação.`;

/** §9.8 adaptado para a falha de `GET /indexes` (lista, não série). */
export const MSG_LISTA_INDICES_INDISPONIVEL =
  'Não foi possível obter a lista de índices. Verifique sua conexão e tente novamente. Se o problema persistir, contate o suporte.';

/** Texto do react-select quando não há opções. */
export const mensagemSemOpcoes = (erroAoCarregar: boolean) =>
  erroAoCarregar ? 'Erro ao carregar' : 'Sem resultados.';

/** AA5 — back não aceitou/ignorou o payload do contrato v2 (CONTRATO_V2 ON × back v1). Texto de implementação. */
export const MSG_CONTRATO_V2_RECUSADO =
  'O servidor não aceitou o cálculo neste formato. Tente novamente em alguns instantes; se o problema persistir, contate o suporte.';

/**
 * BB8-d (PB-16) — o back não declarou suporte ao contrato v2 (`erros/capacidadeV2.ts`).
 * Diferente do AA5: aqui NADA foi enviado, e é isso que a 2ª frase promete. Texto de
 * implementação (aprovado pelo PM).
 */
export const MSG_CAPACIDADE_V2_INDISPONIVEL =
  'O servidor ainda não aceita este formato de cálculo. Nenhum dado foi enviado.';

// ---------------- AA4 (PB-08) — erros tipados do back ----------------

/**
 * Envelope de erro tipado do back (PB-08). Todos os campos são opcionais: enquanto
 * o back não publicar `codigo`, cai no mapa de `msg` de sempre (tolerante).
 */
export interface ErroBack {
  success?: boolean;
  codigo?: string;
  msg?: string;
  detalhes?: Record<string, unknown>;
}

/** Códigos que o front sabe traduzir (PB-08). Qualquer outro cai no mapa de `msg`. */
export const CODIGOS_BACK = [
  'VERSAO_NAO_SUPORTADA',
  'VALIDACAO',
  'INDICE_NAO_PUBLICADO',
  'SERIE_INDISPONIVEL',
] as const;

export type CodigoBack = (typeof CODIGOS_BACK)[number];

/**
 * `msg` de VALIDACAO "parece português"? O back devolve a mensagem de validação
 * já pronta para o usuário, mas as versões antigas mandavam texto técnico em
 * inglês. Heurística: acento/ç ou uma palavra-chave do domínio em PT. Sem isso,
 * usamos o fallback genérico em vez de mostrar inglês para o usuário.
 */
const pareceTextoPt = (msg: string): boolean =>
  /[áàâãéêíóôõúüç]/i.test(msg) ||
  /\b(não|deve|inválid|obrigat|parcela|data|valor|índice)/i.test(msg);

const texto = (v: unknown, padrao = ''): string =>
  v === null || v === undefined ? padrao : String(v);

/**
 * Traduz o erro do back usando `codigo` quando ele existe (PB-08); sem `codigo`
 * (ou com código desconhecido) mantém exatamente o comportamento atual, que é
 * o mapa de `msg` de `traduzirMsgBack`.
 */
export const interpretarErroBack = (data: unknown): MsgTraduzida => {
  if (!data || typeof data !== 'object') return traduzirMsgBack(data);
  const erro = data as ErroBack;
  const detalhes = (erro.detalhes ?? {}) as Record<string, unknown>;
  switch (erro.codigo) {
    case 'VERSAO_NAO_SUPORTADA':
      return { texto: MSG_CONTRATO_V2_RECUSADO, conhecida: true };
    case 'VALIDACAO': {
      const msg = typeof erro.msg === 'string' ? erro.msg.trim() : '';
      return {
        texto: msg !== '' && pareceTextoPt(msg) ? msg : FALLBACK_CALCULO,
        conhecida: true,
      };
    }
    case 'INDICE_NAO_PUBLICADO':
      return {
        texto: msgIndiceNaoPublicado(
          texto(detalhes.indice, 'índice'),
          texto(detalhes.periodo ?? detalhes.mes_ano),
        ),
        conhecida: true,
      };
    case 'SERIE_INDISPONIVEL':
      return {
        texto: msgSerieIndisponivel(
          texto(detalhes.indice, 'índice'),
          texto(detalhes.data_inicial ?? detalhes.inicio),
          texto(detalhes.data_final ?? detalhes.fim),
        ),
        conhecida: true,
      };
    default:
      return traduzirMsgBack(erro.msg);
  }
};
